//jacques-andre-editeur\JAEfrontend\src\components\StripeCheckout.jsx
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useCreateStripePaymentIntentMutation, usePayOrderMutation } from '../slices/ordersApiSlice';
import { setClientSecret, setPaymentStatus, clearCartItems, setOrderId } from '../slices/cartSlice';
import { useEffect, useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const StripeCheckout = ({ orderId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clientSecret } = useSelector((state) => state.cart);

  const [createPaymentIntent] = useCreateStripePaymentIntentMutation();
  const [payOrder] = usePayOrderMutation();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { cartItems, shippingAddress } = useSelector((state) => state.cart);

  // On ne crée le PaymentIntent qu'une seule fois par commande
  useEffect(() => {
    if (!clientSecret && orderId) {
      console.log('Création PaymentIntent pour orderId', orderId);

      createPaymentIntent(orderId)
        .unwrap()
        .then((res) => {
          console.log('backend /stripe response', res);
          dispatch(setClientSecret(res.clientSecret));
          setErrorMessage('');
        })
        .catch((err) => {
          console.error('Erreur init paiement:', err);
          setErrorMessage("Erreur lors de l'initialisation du paiement.");
        });
    }
  }, [orderId]); // on enlève clientSecret, createPaymentIntent, dispatch

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!stripe || !elements || !clientSecret) {
      setErrorMessage("Erreur Stripe : configuration incomplète.");
      return;
    }
    if (loading) return; // protection double-clic
    setLoading(true);

    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Champ carte introuvable.');

      console.log('Confirming payment with clientSecret', clientSecret);

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });

      // Log structuré avec ID et statut
      console.log('🔎 stripe.confirmCardPayment result', {
        id: result?.paymentIntent?.id || result?.error?.payment_intent?.id,
        status: result?.paymentIntent?.status || result?.error?.payment_intent?.status,
        raw: result,
      });

      // === Cas d'erreur ===
      if (result.error) {
        if (result.error.code === 'payment_intent_unexpected_state' && result.error.payment_intent) {
          const pi = result.error.payment_intent;
          console.warn('payment_intent_unexpected_state, PI:', pi);

          if (pi.status === 'succeeded') {
            try {
              await payOrder({
                orderId,
                details: {
                  id: pi.id,
                  status: pi.status,
                  amount_received: pi.amount,
                  currency: pi.currency,
                  created: pi.created,
                },
              }).unwrap();

              dispatch(clearCartItems());
              dispatch(setClientSecret(null));
              dispatch(setPaymentStatus('succeeded'));
              dispatch(setOrderId(null));
              navigate('/', { state: { success: 'Votre paiement a bien été validé' } });
              return;
            } catch (err) {
              console.error('Erreur update backend après PI déjà succeeded:', err);
              setErrorMessage('Paiement validé par Stripe mais impossible d’enregistrer la commande côté serveur.');
              setLoading(false);
              return;
            }
          }
        }

        setErrorMessage(result.error.message || 'Erreur lors du paiement.');
        setLoading(false);
        return;
      }

      // === Cas succès normal ===
      const paymentIntent = result.paymentIntent;
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        try {
          await payOrder({
            orderId,
            details: {
              id: paymentIntent.id,
              status: paymentIntent.status,
              amount_received: paymentIntent.amount_received || paymentIntent.amount,
              currency: paymentIntent.currency,
              receipt_email: paymentIntent.receipt_email || '',
              created: paymentIntent.created,
            },
          }).unwrap();
        } catch (err) {
          console.error('Erreur update order to paid:', err);
          setErrorMessage('Paiement réussi mais impossible d’enregistrer la commande côté serveur.');
          dispatch(clearCartItems());
          dispatch(setClientSecret(null));
          dispatch(setPaymentStatus('succeeded'));
          setLoading(false);
          return;
        }

        // tout OK
        dispatch(clearCartItems());
        dispatch(setClientSecret(null));
        dispatch(setPaymentStatus('succeeded'));
        dispatch(setOrderId(null));
        navigate('/', { state: { success: 'Votre paiement a bien été validé' } });
      } else {
        setErrorMessage(`Statut du paiement : ${paymentIntent?.status || 'inconnu'}`);
      }
    } catch (err) {
      console.error('Exception handleSubmit:', err);
      setErrorMessage(err.message || 'Une erreur de traitement est survenue.');
    }

    setLoading(false);
  };

  return (
    <div className="mt-4">
      <h3>Paiement sécurisé par carte</h3>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <form onSubmit={handleSubmit}>
        <CardElement
          className="p-3 border rounded"
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#32325d',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#fa755a' },
            },
            hidePostalCode: true,
          }}
        />

        <Button type="submit" disabled={!stripe || !clientSecret || loading} className="mt-3">
          {loading ? 'Paiement en cours...' : 'Payer'}
        </Button>
      </form>
    </div>
  );
};

export default StripeCheckout;
